import React, {useEffect, useState, useRef} from 'react';
import {motion} from 'framer-motion';
import clsx from 'clsx';

interface ChannelNavbarProps {
  size?: 'xs' | 'sm' | 'md';
  background?: string;
  color?: string;
  shadow?: 'sm' | 'shadow' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  children?: React.ReactNode;
  fixed?: boolean;
  sticky?: boolean;
  absolute?: boolean;
}

const ChannelNavbar: React.FC<ChannelNavbarProps> = ({
  size = 'md',
  background,
  color,
  shadow,
  className,
  children,
  fixed,
  sticky,
  absolute,
}) => {
  const [offset, setOffset] = useState(0);

  // Ref pour la valeur brute de scrollTop
  const lastRawY = useRef(0);
  // Ref pour la valeur clampée
  const lastY = useRef(0);
  // Cumul de pixels montants
  const pendingUp = useRef(0);
  // Flag mode révélation
  const revealMode = useRef(false);

  useEffect(() => {
    const mainEl = document.querySelector('main');
    if (!mainEl) return;

    const heights = {xs: 48, sm: 54, md: 64};
    const navH = heights[size] ?? heights.md;
    const maxOffset = navH * 2;
    const THRESHOLD = 20; // px à remonter pour révéler

    const onScroll = () => {
      const rawY = mainEl.scrollTop;
      const maxScroll = mainEl.scrollHeight - mainEl.clientHeight;

      // 1) détecter l’overscroll (rebond) au top/bottom
      const deltaRaw = rawY - lastRawY.current;
      const overscrollTop = rawY <= 0 && deltaRaw < 0;
      const overscrollBottom = rawY >= maxScroll && deltaRaw > 0;
      if (overscrollTop || overscrollBottom) {
        // on ignore ces events
        lastRawY.current = rawY;
        return;
      }

      // 2) clamp pour calculer un vrai scrollY entre [0, maxScroll]
      const clampedY = Math.max(0, Math.min(rawY, maxScroll));
      const delta = clampedY - lastY.current;
      const canHide = clampedY >= navH;

      // 3) scroll down → on cache la navbar
      if (delta > 0 && canHide) {
        revealMode.current = false;
        pendingUp.current = 0;
        setOffset(prev => Math.min(prev + delta, maxOffset));

        // 4) scroll up → on attend le THRESHOLD avant de révéler
      } else if (delta < 0) {
        pendingUp.current += -delta;

        if (revealMode.current) {
          // déjà en mode révélation → on continue de remonter
          setOffset(prev => Math.max(prev + delta, 0));
        } else if (pendingUp.current >= THRESHOLD) {
          // seuil atteint → on active la révélation
          revealMode.current = true;
          setOffset(prev => Math.max(prev + delta, 0));
        }
      }

      // 5) on sauve les deux pour le prochain événement
      lastRawY.current = rawY;
      lastY.current = clampedY;
    };

    mainEl.addEventListener('scroll', onScroll, {passive: true});
    return () => mainEl.removeEventListener('scroll', onScroll);
  }, [size]);

  // calcul opacité
  const heights = {xs: 48, sm: 54, md: 64};
  const maxOffset = (heights[size] ?? heights.md) * 2;
  const opacity = 1 - offset / maxOffset;

  // classes utilitaires
  const sizeClasses = {
    xs: 'h-48 py-4 px-8 md:px-30',
    sm: 'h-54 py-8 px-16 md:px-30',
    md: 'h-64 py-16 px-16 md:px-30',
  };
  const bgCls = background
    ? background.startsWith('bg')
      ? background
      : `bg-${background}`
    : '';
  const txtCls = color
    ? color.startsWith('text')
      ? color
      : `text-${color}`
    : '';
  const shadowCl = shadow ? `shadow-${shadow}` : '';
  const posCl = clsx({
    'fixed top-0 left-0 w-full z-20': fixed,
    'sticky top-0 left-0 w-full z-20': sticky,
    'absolute top-0 left-0 w-full z-20': absolute,
    'mx-[-1rem] mt-[-1rem]': !fixed && !sticky && !absolute,
  });

  return (
    <motion.div
      style={{transform: `translateY(${-offset}px)`, opacity}}
      className={clsx(posCl, className)}
    >
      <div className={clsx(bgCls, txtCls, shadowCl, sizeClasses[size])}>
        {children}
      </div>
    </motion.div>
  );
};

export default ChannelNavbar;
